import React from 'react';

function stageCountEntries(summary) {
  return [
    ['review_improvement_count', 'Review improvement'],
    ['validation_ready_count', 'Validation ready'],
    ['awaiting_post_fix_evidence_count', 'Awaiting evidence'],
    ['plan_needed_count', 'Plan needed'],
    ['diagnosed_count', 'Diagnosed'],
    ['monitoring_count', 'Monitoring'],
  ].map(([key, label]) => ({
    key,
    label,
    value: summary?.[key] || 0,
  }));
}

function actionElement(loop, className = 'secondary-button action-link-button') {
  if (!loop?.next_action_href) {
    return null;
  }
  const scrollTargetId = (() => {
    if (loop.supporting_refs?.active_improvement_plan_id || loop.next_action_type === 'create_plan') {
      return 'improvement-plan-section';
    }
    if (loop.next_action_type === 'create_validation_pack') {
      return 'targeted-validation-section';
    }
    if (loop.next_action_type === 'inspect_evidence' || loop.next_action_label === 'Inspect latest evidence') {
      return 'finding-evidence-section';
    }
    return null;
  })();
  const currentRoute = window.location.hash.split('?')[0];
  const targetRoute = loop.next_action_href.split('?')[0];
  if (scrollTargetId && currentRoute === targetRoute) {
    return (
      <button
        className={className}
        type="button"
        onClick={() => {
          document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {loop.next_action_label}
      </button>
    );
  }
  return (
    <a className={className} href={loop.next_action_href}>
      {loop.next_action_label}
    </a>
  );
}

export function FindingImprovementLoopPanel({ loop, showAction = true }) {
  if (!loop) {
    return null;
  }
  return (
    <section className="detail-section improvement-loop-section">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Improvement loop</p>
          <h3>Next evidence-backed step</h3>
        </div>
      </div>
      <div className="detail-card improvement-loop-card">
        <div className="workflow-mode-header">
          <div>
            <strong>{loop.label}</strong>
            <p>{loop.summary}</p>
          </div>
          <span className="chip passive-meta-chip">{loop.stage.split('_').join(' ')}</span>
        </div>
        {loop.warnings?.length ? (
          <div className="stack compact-stack improvement-loop-warnings">
            {loop.warnings.map((warning) => (
              <p className="overview-note" key={warning}>
                {warning}
              </p>
            ))}
          </div>
        ) : null}
        {showAction ? <div className="form-actions">{actionElement(loop, 'primary-button action-link-button')}</div> : null}
      </div>
    </section>
  );
}

export function WorkflowImprovementLoopPanel({ summary }) {
  if (!summary) {
    return null;
  }
  const stageCounts = stageCountEntries(summary);
  const topItems = summary.top_items || [];

  return (
    <section className="detail-section improvement-loop-section">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Improvement loop</p>
          <h3>Active finding progress</h3>
        </div>
      </div>
      <div className="detail-metrics-grid improvement-loop-counts">
        {stageCounts.map((entry) => (
          <div className="detail-card detail-stat-card" key={entry.key}>
            <strong>{entry.value}</strong>
            <p>{entry.label}</p>
          </div>
        ))}
      </div>
      {topItems.length ? (
        <div className="stack compact-stack">
          {topItems.map((item) => (
            <article className="detail-card workflow-link-card" key={item.finding_id}>
              <div className="workflow-mode-header">
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.summary}</p>
                </div>
                <span className="chip passive-meta-chip">{item.label}</span>
              </div>
              {item.warnings?.length ? <p className="overview-note">{item.warnings[0]}</p> : null}
              <div className="finding-progress-meta">
                <span>{item.severity}</span>
                <span>{item.status}</span>
              </div>
              <div className="form-actions">{actionElement(item)}</div>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state-card">
          <strong>No active improvement loop items</strong>
          <p>Active finding progress will appear here when this workflow has findings to improve.</p>
        </div>
      )}
    </section>
  );
}
